import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Spinner, Badge, Modal, InputGroup } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import axios from 'axios';

const MultasManagement = () => {
  const { hasPermission } = useAuth();
  const puedeEditar = hasPermission('socios', 'update');

  const [socios, setSocios] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aplicando, setAplicando] = useState(false);

  // Formulario de aplicación masiva
  const [busquedaSocio, setBusquedaSocio] = useState('');
  const [seleccionados, setSeleccionados] = useState([]);
  const [monto, setMonto] = useState('');
  const [motivo, setMotivo] = useState('');
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [observaciones, setObservaciones] = useState('');

  // Detalle por socio
  const [detalleSocio, setDetalleSocio] = useState(null);
  const [showDetalle, setShowDetalle] = useState(false);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  // Edición de una multa individual dentro del detalle
  const [editandoId, setEditandoId] = useState(null);
  const [editForm, setEditForm] = useState({ monto: '', motivo: '', fecha: '', observaciones: '' });
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);

  const fetchSocios = useCallback(async () => {
    try {
      const response = await axios.get('/api/users?role=socio&limit=1000');
      setSocios(response.data.users || []);
    } catch (error) {
      console.error('Error fetching socios:', error);
      toast.error('Error al cargar socios');
    }
  }, []);

  const fetchResumen = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/multas/resumen');
      setResumen(response.data.data || []);
    } catch (error) {
      console.error('Error fetching resumen de multas:', error);
      toast.error('Error al cargar el resumen de multas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSocios();
    fetchResumen();
  }, [fetchSocios, fetchResumen]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const sociosFiltrados = socios.filter(s => {
    const texto = busquedaSocio.toLowerCase();
    return (s.name || '').toLowerCase().includes(texto) || (s.cedula || '').includes(busquedaSocio);
  });

  const toggleSeleccionado = (id) => {
    setSeleccionados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const seleccionarTodos = () => {
    const idsFiltrados = sociosFiltrados.map(s => s._id);
    const todosYaSeleccionados = idsFiltrados.length > 0 && idsFiltrados.every(id => seleccionados.includes(id));
    if (todosYaSeleccionados) {
      setSeleccionados(prev => prev.filter(id => !idsFiltrados.includes(id)));
    } else {
      setSeleccionados(prev => Array.from(new Set([...prev, ...idsFiltrados])));
    }
  };

  const handleAplicar = async () => {
    if (!puedeEditar) {
      toast.error('No tienes permisos para aplicar multas');
      return;
    }
    if (seleccionados.length === 0) {
      toast.error('Selecciona al menos un socio');
      return;
    }
    const montoNum = parseFloat(monto);
    if (isNaN(montoNum) || montoNum <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    if (!motivo.trim()) {
      toast.error('Ingresa el motivo de la multa');
      return;
    }

    setAplicando(true);
    try {
      const response = await axios.post('/api/multas/bulk', {
        socios: seleccionados,
        monto: montoNum,
        motivo: motivo.trim(),
        fecha,
        observaciones: observaciones.trim()
      });
      toast.success(response.data.message || 'Multas aplicadas exitosamente');
      setSeleccionados([]);
      setMonto('');
      setMotivo('');
      setObservaciones('');
      await fetchResumen();
    } catch (error) {
      console.error('Error al aplicar multas:', error);
      toast.error(error.response?.data?.message || 'Error al aplicar las multas');
    } finally {
      setAplicando(false);
    }
  };

  const verDetalle = async (fila) => {
    setLoadingDetalle(true);
    setShowDetalle(true);
    setEditandoId(null);
    setDetalleSocio({ nombre: fila.nombre, socioId: fila.socioId, multas: [] });
    try {
      const response = await axios.get(`/api/multas/socio/${fila.socioId}`);
      setDetalleSocio({ nombre: fila.nombre, socioId: fila.socioId, multas: response.data.data || [] });
    } catch (error) {
      console.error('Error fetching detalle de multas:', error);
      toast.error('Error al cargar el detalle');
    } finally {
      setLoadingDetalle(false);
    }
  };

  const recargarDetalle = async () => {
    if (!detalleSocio) return;
    await verDetalle({ nombre: detalleSocio.nombre, socioId: detalleSocio.socioId });
  };

  const marcarPagada = async (multaId) => {
    try {
      await axios.patch(`/api/multas/${multaId}/pagar`);
      toast.success('Multa marcada como pagada');
      await recargarDetalle();
      await fetchResumen();
    } catch (error) {
      console.error('Error al marcar pagada:', error);
      toast.error('Error al actualizar la multa');
    }
  };

  const eliminarMulta = async (multaId) => {
    if (!window.confirm('¿Eliminar esta multa? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`/api/multas/${multaId}`);
      toast.success('Multa eliminada');
      await recargarDetalle();
      await fetchResumen();
    } catch (error) {
      console.error('Error al eliminar multa:', error);
      toast.error('Error al eliminar la multa');
    }
  };

  const iniciarEdicion = (m) => {
    setEditandoId(m._id);
    setEditForm({
      monto: m.monto,
      motivo: m.motivo,
      fecha: new Date(m.fecha).toISOString().slice(0, 10),
      observaciones: m.observaciones || ''
    });
  };

  const cancelarEdicion = () => {
    setEditandoId(null);
    setEditForm({ monto: '', motivo: '', fecha: '', observaciones: '' });
  };

  const guardarEdicion = async (multaId) => {
    const montoNum = parseFloat(editForm.monto);
    if (isNaN(montoNum) || montoNum < 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    if (!editForm.motivo.trim()) {
      toast.error('El motivo es obligatorio');
      return;
    }

    setGuardandoEdicion(true);
    try {
      await axios.put(`/api/multas/${multaId}`, {
        monto: montoNum,
        motivo: editForm.motivo.trim(),
        fecha: editForm.fecha,
        observaciones: editForm.observaciones.trim()
      });
      toast.success('Multa actualizada');
      cancelarEdicion();
      await recargarDetalle();
      await fetchResumen();
    } catch (error) {
      console.error('Error al editar multa:', error);
      toast.error(error.response?.data?.message || 'Error al actualizar la multa');
    } finally {
      setGuardandoEdicion(false);
    }
  };

  const totalPendienteGeneral = resumen.reduce((sum, r) => sum + (r.totalPendiente || 0), 0);
  const totalPagadoGeneral = resumen.reduce((sum, r) => sum + (r.totalPagada || 0), 0);
  const totalGeneral = resumen.reduce((sum, r) => sum + (r.totalGeneral || 0), 0);

  if (loading && resumen.length === 0 && socios.length === 0) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <div className="fade-in">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body>
                <h2 className="text-gradient mb-2">
                  <i className="fas fa-gavel me-2"></i>
                  Multas
                </h2>
                <p className="text-muted mb-0">
                  Aplica una multa a uno o varios socios a la vez. Los totales se suman automáticamente por socio.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Totales generales */}
        <Row className="mb-4">
          <Col md={4}>
            <Card className="stats-card border-danger">
              <Card.Body>
                <div className="stats-icon text-danger"><i className="fas fa-exclamation-triangle"></i></div>
                <div className="stats-number text-danger">{formatCurrency(totalPendienteGeneral)}</div>
                <div className="stats-label">Total Pendiente (todos los socios)</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="stats-card border-success">
              <Card.Body>
                <div className="stats-icon text-success"><i className="fas fa-check-circle"></i></div>
                <div className="stats-number text-success">{formatCurrency(totalPagadoGeneral)}</div>
                <div className="stats-label">Total Pagado (todos los socios)</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="stats-card border-primary">
              <Card.Body>
                <div className="stats-icon text-primary"><i className="fas fa-gavel"></i></div>
                <div className="stats-number text-primary">{formatCurrency(totalGeneral)}</div>
                <div className="stats-label">Total General de Multas</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Aplicar multa a uno o varios socios */}
        {puedeEditar && (
          <Row className="mb-4">
            <Col>
              <Card className="card-custom">
                <Card.Header className="card-header-custom">
                  <h5 className="mb-0"><i className="fas fa-gavel me-2"></i>Aplicar Multa</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={5}>
                      <Form.Group className="mb-3">
                        <Form.Label>Buscar socio</Form.Label>
                        <InputGroup>
                          <InputGroup.Text><i className="fas fa-search"></i></InputGroup.Text>
                          <Form.Control
                            placeholder="Nombre o cédula..."
                            value={busquedaSocio}
                            onChange={(e) => setBusquedaSocio(e.target.value)}
                          />
                        </InputGroup>
                      </Form.Group>

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Button variant="outline-secondary" size="sm" onClick={seleccionarTodos}>
                          Seleccionar / deseleccionar todos
                        </Button>
                        <Badge bg="primary">{seleccionados.length} seleccionado{seleccionados.length !== 1 ? 's' : ''}</Badge>
                      </div>

                      <div style={{ maxHeight: '320px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '8px', padding: '10px' }}>
                        {sociosFiltrados.length === 0 ? (
                          <p className="text-muted text-center mb-0 py-3">No hay socios que coincidan</p>
                        ) : (
                          sociosFiltrados.map(socio => (
                            <Form.Check
                              key={socio._id}
                              type="checkbox"
                              id={`multa-socio-${socio._id}`}
                              className="mb-2"
                              label={
                                <span>
                                  <strong>{socio.name}</strong>{' '}
                                  <small className="text-muted">({socio.socioInfo?.numeroSocio || 'N/A'})</small>
                                </span>
                              }
                              checked={seleccionados.includes(socio._id)}
                              onChange={() => toggleSeleccionado(socio._id)}
                            />
                          ))
                        )}
                      </div>
                    </Col>

                    <Col md={7}>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Monto ($)</Form.Label>
                            <Form.Control
                              type="number"
                              min="0"
                              step="0.01"
                              value={monto}
                              onChange={(e) => setMonto(e.target.value)}
                              placeholder="0.00"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Fecha</Form.Label>
                            <Form.Control
                              type="date"
                              value={fecha}
                              onChange={(e) => setFecha(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Form.Group className="mb-3">
                        <Form.Label>Motivo</Form.Label>
                        <Form.Control
                          type="text"
                          value={motivo}
                          onChange={(e) => setMotivo(e.target.value)}
                          placeholder="Ej: Inasistencia a la reunión de octubre"
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Observaciones (opcional)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={observaciones}
                          onChange={(e) => setObservaciones(e.target.value)}
                        />
                      </Form.Group>

                      <Button
                        variant="danger"
                        onClick={handleAplicar}
                        disabled={aplicando || seleccionados.length === 0}
                      >
                        {aplicando ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <>
                            <i className="fas fa-gavel me-2"></i>
                            Aplicar multa a {seleccionados.length} socio{seleccionados.length !== 1 ? 's' : ''} seleccionado{seleccionados.length !== 1 ? 's' : ''}
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Resumen por socio */}
        <Row>
          <Col>
            <Card className="card-custom">
              <Card.Header className="card-header-custom">
                <h5 className="mb-0"><i className="fas fa-list me-2"></i>Multas por Socio</h5>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>Socio</th>
                        <th>Cédula</th>
                        <th className="text-center"># Multas</th>
                        <th className="text-end">Pendiente</th>
                        <th className="text-end">Pagado</th>
                        <th className="text-end">Total</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumen.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center text-muted py-4">
                            <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
                            Aún no hay multas registradas
                          </td>
                        </tr>
                      ) : (
                        resumen.map(fila => (
                          <tr key={fila.socioId}>
                            <td>
                              <div className="fw-bold">{fila.nombre}</div>
                              <small className="text-muted">{fila.email}</small>
                            </td>
                            <td>{fila.cedula}</td>
                            <td className="text-center"><Badge bg="secondary">{fila.cantidad}</Badge></td>
                            <td className="text-end text-danger fw-bold">{formatCurrency(fila.totalPendiente)}</td>
                            <td className="text-end text-success">{formatCurrency(fila.totalPagada)}</td>
                            <td className="text-end fw-bold">{formatCurrency(fila.totalGeneral)}</td>
                            <td className="text-center">
                              <Button variant="outline-primary" size="sm" onClick={() => verDetalle(fila)}>
                                <i className="fas fa-eye me-1"></i>Ver
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {resumen.length > 0 && (
                      <tfoot>
                        <tr className="table-light fw-bold">
                          <td colSpan="3">Total general</td>
                          <td className="text-end text-danger">{formatCurrency(totalPendienteGeneral)}</td>
                          <td className="text-end text-success">{formatCurrency(totalPagadoGeneral)}</td>
                          <td className="text-end">{formatCurrency(totalGeneral)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Modal de detalle por socio */}
      <Modal show={showDetalle} onHide={() => { setShowDetalle(false); cancelarEdicion(); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-gavel me-2"></i>
            Multas de {detalleSocio?.nombre}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetalle ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" />
            </div>
          ) : detalleSocio && detalleSocio.multas.length === 0 ? (
            <p className="text-muted text-center mb-0">Este socio no tiene multas registradas</p>
          ) : (
            <div className="table-responsive">
              <Table size="sm" hover>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Motivo</th>
                    <th className="text-end">Monto</th>
                    <th className="text-center">Estado</th>
                    {puedeEditar && <th className="text-center">Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {detalleSocio?.multas.map(m => {
                    const enEdicion = editandoId === m._id;

                    if (enEdicion) {
                      return (
                        <tr key={m._id} className="table-warning">
                          <td>
                            <Form.Control
                              type="date"
                              size="sm"
                              value={editForm.fecha}
                              onChange={(e) => setEditForm(prev => ({ ...prev, fecha: e.target.value }))}
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="text"
                              size="sm"
                              className="mb-1"
                              value={editForm.motivo}
                              onChange={(e) => setEditForm(prev => ({ ...prev, motivo: e.target.value }))}
                              placeholder="Motivo"
                            />
                            <Form.Control
                              type="text"
                              size="sm"
                              value={editForm.observaciones}
                              onChange={(e) => setEditForm(prev => ({ ...prev, observaciones: e.target.value }))}
                              placeholder="Observaciones (opcional)"
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              size="sm"
                              min="0"
                              step="0.01"
                              value={editForm.monto}
                              onChange={(e) => setEditForm(prev => ({ ...prev, monto: e.target.value }))}
                            />
                          </td>
                          <td className="text-center">
                            <Badge bg={m.estado === 'pagada' ? 'success' : 'warning'}>
                              {m.estado === 'pagada' ? 'Pagada' : 'Pendiente'}
                            </Badge>
                          </td>
                          {puedeEditar && (
                            <td className="text-center">
                              <Button
                                variant="success"
                                size="sm"
                                className="me-1"
                                onClick={() => guardarEdicion(m._id)}
                                disabled={guardandoEdicion}
                                title="Guardar cambios"
                              >
                                {guardandoEdicion ? <Spinner animation="border" size="sm" /> : <i className="fas fa-save"></i>}
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={cancelarEdicion}
                                disabled={guardandoEdicion}
                                title="Cancelar"
                              >
                                <i className="fas fa-times"></i>
                              </Button>
                            </td>
                          )}
                        </tr>
                      );
                    }

                    return (
                      <tr key={m._id}>
                        <td>{new Date(m.fecha).toLocaleDateString('es-ES')}</td>
                        <td>
                          {m.motivo}
                          {m.observaciones && <div><small className="text-muted">{m.observaciones}</small></div>}
                        </td>
                        <td className="text-end fw-bold">{formatCurrency(m.monto)}</td>
                        <td className="text-center">
                          <Badge bg={m.estado === 'pagada' ? 'success' : 'warning'}>
                            {m.estado === 'pagada' ? 'Pagada' : 'Pendiente'}
                          </Badge>
                        </td>
                        {puedeEditar && (
                          <td className="text-center">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-1"
                              onClick={() => iniciarEdicion(m)}
                              title="Editar"
                            >
                              <i className="fas fa-pen"></i>
                            </Button>
                            {m.estado !== 'pagada' && (
                              <Button
                                variant="outline-success"
                                size="sm"
                                className="me-1"
                                onClick={() => marcarPagada(m._id)}
                                title="Marcar como pagada"
                              >
                                <i className="fas fa-check"></i>
                              </Button>
                            )}
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => eliminarMulta(m._id)}
                              title="Eliminar"
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowDetalle(false); cancelarEdicion(); }}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MultasManagement;
